<template>
  <div class="flex">
    <div class="flex-shrink-0" :style="{ paddingRight: '37.5rem' }">
      <div
        class="sticky left-0 z-30 flex items-center justify-between px-6 h-14 top-24 bg-slate-300"
        :style="{ width: 'calc(100vw - 37.5rem - 0.5rem)' }"
      >
        <div>
          <BlTextBody2
            :text="`Showing ${bookingTimeslots.length} appointment schedule(s)`"
          />
        </div>
        <div class="flex items-center gap-2">
          <div>
            <BlSelectBox
              v-model="timeslotView"
              :options="timeslotViewOptions"
              dense
              @change="$emit('set-timeslot-view', $event)"
            />
          </div>
          <BlTextBody2 :text="masterDate" />
        </div>
      </div>
      <div class="sticky z-20 flex h-12 top-38 bg-slate-200">
        <div
          class="sticky left-0 z-10 flex-shrink-0 w-20 h-12 border-b bg-slate-200 border-slate-400"
        ></div>
        <div
          v-for="(n, idx) in headers"
          :key="idx"
          class="flex items-center justify-center flex-shrink-0 h-12 px-2 py-1 border-b border-slate-400"
          :style="{ width: `calc(${slotWidth})` }"
        >
          <div v-if="n.highlight" class="flex items-center gap-1 h-7">
            <BlTextBody2 :text="n.label.split(' ')[0]" />
            <div
              class="flex items-center justify-center rounded-full h-7 w-7 bg-light-purple-400"
            >
              <BlTextBody2
                :text="n.label.split(' ').pop()"
                class="text-pure-white"
              />
            </div>
          </div>
          <div v-else class="px-4 truncate h-7" :title="n.label">
            <BlTextBody2 :text="n.label" class="truncate" />
          </div>
        </div>
      </div>
      <div class="relative flex flex-col bg-slate-200">
        <div
          v-for="time in clockRound"
          :key="time"
          class="flex"
          :id="`recurring_time_view_${time}`"
        >
          <div
            class="sticky left-0 z-10 flex justify-end flex-shrink-0 w-20 px-3 py-2 transition-all duration-300 border-b border-r bg-slate-200 border-slate-400"
            :style="{ height: `${slotHeight * 2}rem` }"
          >
            <BlTextBody2 :text="time" />
          </div>
          <div
            v-for="(n, idx) in headers"
            :key="idx"
            class="flex flex-col items-center justify-center flex-shrink-0 gap-2 transition-all duration-300 border-b border-r border-slate-400 bg-slate-200"
            :style="{
              height: `${slotHeight * 2}rem`,
              width: `calc(${slotWidth})`
            }"
          ></div>
        </div>
      </div>
    </div>
    <div
      class="fixed z-50 flex-shrink-0 overflow-y-auto border top-24 right-2 bg-pure-white border-slate-400 scrollbar-lg"
      :style="{
        height: 'calc(100vh - 6rem - 5rem - 0.5rem)',
        width: '37.5rem'
      }"
    >
      <div
        class="sticky top-0 z-10 flex flex-col gap-1 px-6 py-4 border-b bg-pure-white border-slate-400"
      >
        <BlTextSubtitle1 text="Manage timeslots and set recurrence" />
        <div>
          <BlTextBody2
            text="You are now creating weekly recurring schedules for "
            class="text-slate-800"
          />
          <BlTextSubtitle3
            v-if="client"
            :text="`${client.name}. `"
            class="text-slate-800"
          />
          <BlTextBody2
            text="Below, you can review and add additional timeslots, and configure the recurrence settings."
            class="text-slate-800"
          />
        </div>
      </div>
      <div class="flex flex-col gap-3 px-6 pt-4 pb-54">
        <BlTextSubtitle1 text="Manage timeslots" />
        <div class="grid items-center grid-cols-2 gap-4">
          <BlInputButton
            type="outlined"
            dense
            label="Select from previous schedule"
            @clicked="$emit('open-previous')"
          />
          <BlInputButton
            icon="plus"
            dense
            label="Add a timeslot"
            @clicked="$emit('add-timeslot')"
          />
        </div>
        <BlTextBody2 :text="`${bookingTimeslots.length} timeslot(s) added`" />
        <div :key="`timeslots_${bookingTimeslots.length}`">
          <ItemTimeslot
            v-for="(timeslot, idx) in bookingTimeslots"
            :key="timeslot.key"
            :timeslot="timeslot"
            isPattern
            :deleteDisabled="idx === 0"
            useEdit
            useStartsFrom
            @edit="$emit('edit-timeslot', timeslot)"
            @delete="$emit('delete-timeslot', $event)"
          />
        </div>
      </div>

      <div
        class="fixed z-10 flex flex-col gap-3 px-6 py-4 border-t bottom-22 bg-pure-white border-slate-400"
        :style="{ width: '36.75rem' }"
      >
        <BlTextSubtitle1 text="Recurrence settings" />
        <div class="flex flex-col gap-2">
          <div class="flex gap-4">
            <div class="flex items-center flex-shrink-0 h-11 w-34">
              <BlTextSubtitle3 text="Repeat every" />
            </div>
            <div class="flex items-center gap-2 h-11">
              <BlInputText
                class="w-16"
                :input="intervalInput"
                @changed="onInputInterval"
              />
              <BlTextBody2 text="week(s)" class="text-slate-800" />
            </div>
          </div>
          <div class="flex gap-4">
            <div class="flex items-center flex-shrink-0 h-11 w-34">
              <BlTextSubtitle3 text="Total appointments" />
            </div>
            <div class="flex-col gap-2">
              <div class="flex items-center gap-3 h-11">
                <BlInputRadio
                  name="end_after"
                  :checked="endAfterInput === 'based_on_available_credits'"
                  @changed="endAfterInput = 'based_on_available_credits'"
                />
                <BlTextBody2 text="Based on available credits" />
                <div
                  class="flex items-center px-4 border rounded h-11 bg-slate-200 border-slate-500"
                >
                  <BlTextBody2 :text="`${availableCredits} credit(s)`" />
                </div>
              </div>
              <div class="flex items-center gap-3 h-11">
                <BlInputRadio
                  name="end_after"
                  :checked="endAfterInput === 'appointment_count'"
                  @changed="endAfterInput = 'appointment_count'"
                />
                <BlInputText
                  class="self-start w-16"
                  :input="appointmentCountInput"
                  @changed="onInputAppointmentCount"
                />
                <BlTextBody2 text="appointment(s)" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <SlotRecurring
      v-for="(slot, idx) in existingSlots"
      :key="`${idx}_${slot.id}`"
      :isCompact="timeslotView === 'compact'"
      :period="slot.period"
      :therapist="slot.user"
      :room="slot.room"
      :position="slot.position"
      :assets="slot.assets"
      :error="slot.error"
    />
    <SlotRecurring
      v-for="(slot, idx) in existingAppointments"
      :key="`${idx}_${slot.id}`"
      :isCompact="timeslotView === 'compact'"
      :period="slot.period"
      :therapist="slot.user"
      :room="slot.room"
      :position="slot.position"
      :assets="slot.assets"
      :style="{ zIndex: 1 }"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import { CLOCK_ROUND } from "@/plugins/assets.js";

import SlotRecurring from "./SlotRecurring.vue";
import ItemTimeslot from "./ItemTimeslot.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextSubtitle4 from "../../BlTextSubtitle4.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlBreadcrumbs from "../../BlBreadcrumbs.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlSelectBox from "../../BlSelectBox.vue";

export default {
  name: "ManageScheduleRecurring",
  components: {
    SlotRecurring,
    ItemTimeslot,
    BlTextTitle4,
    BlTextSubtitle1,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextSubtitle4,
    BlTextBody2,
    BlBreadcrumbs,
    BlIcon,
    BlInputButton,
    BlInputToggle,
    BlInputText,
    BlInputRadio,
    BlSelectBox
  },
  props: {
    timeslotView: { type: String },
    timeslotViewOptions: { type: Array },
    // $emit('set-timeslot-view')
    clientAppointments: { type: Array, default: () => [] },
    recurrence: { type: Object }
    // $emit('set-recurrence')
    // $emit('add-timeslot')
    // $emit('edit-timeslot')
    // $emit('delete-timeslot')
    // $emit('open-previous')
  },
  data() {
    return {
      intervalInput: { model: "", inputType: "number", min: 1 },
      /** based_on_available_credits, appointment_count */
      endAfterInput: "based_on_available_credits",
      appointmentCountInput: {
        model: "",
        inputType: "number",
        min: 1,
        max: 50,
        disabled: true,
        error: ""
      },
      clockRound: CLOCK_ROUND
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "client",
      "bookingTimeslots"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    masterDate() {
      if (!this.bookingTimeslots.length) return "";
      const date = this.bookingTimeslots[0].date || "";
      const start = moment(date).format("ddd, DD MMM YYYY");
      const end = moment(date)
        .add(6, "days")
        .format("ddd, DD MMM YYYY");
      return `${start} — ${end}`;
    },
    slotHeight() {
      // retun value in rem, 1 slot is 30 minutes
      if (this.timeslotView === "compact") return 6.25 / 2;
      return 15 / 2;
    },
    slotWidth() {
      return `(100vw - 18.75rem - 5rem - 0.5rem) / ${this.headers.length}`;
    },
    headers() {
      if (!this.bookingTimeslots.length) return [];
      const date = this.bookingTimeslots[0].date || "";
      const startDate = moment(date).format("YYYY-MM-DD");
      const endDate = moment(date)
        .add(6, "days")
        .format("YYYY-MM-DD");

      let current = moment(startDate);

      const dates = [];

      while (current.isSameOrBefore(endDate)) {
        const isSame =
          current.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");
        dates.push({
          highlight: isSame,
          value: current.format("YYYY-MM-DD"),
          label: current.format("ddd DD")
        });
        current.add(1, "days");
      }

      return dates;
    },
    existingSlots() {
      const arr = [...this.bookingTimeslots];
      return arr.map(i => {
        const date = i.date;
        const start = i.start_time_string;
        const end = i.end_time_string;
        const count = this.getSlotCount(start, end);
        const { top, left } = this.getSlotPosition(date, start);

        let isConflicted = false;

        const unavailableCredit =
          (i.booking_review_details &&
            i.booking_review_details.unavailable_credit) ||
          [];

        if (unavailableCredit.length) {
          let packageItemId = 0;
          if (i.credit_transaction_services.length) {
            packageItemId = i.credit_transaction_services[0].package_item_id;
          }
          if (unavailableCredit[0].package_item_id === packageItemId) {
            isConflicted = true;
          }
        }

        const unavailableTherapist =
          (i.booking_review_details &&
            i.booking_review_details.unavailable_therapist) ||
          [];
        const unavailableTherapistKeys = unavailableTherapist.map(i =>
          JSON.stringify(i)
        );

        const unavailableTimeslots =
          (i.booking_review_details &&
            i.booking_review_details.unavailable_timeslots) ||
          [];
        const unavailableTimeslotsKeys = unavailableTimeslots.map(i =>
          JSON.stringify(i)
        );

        // don't change object key order -- checking for similarity timeslots
        const currentSlotKey = JSON.stringify({
          date: i.date,
          room_id: i.room.id,
          user_id: (i.user && i.user.id) || 0,
          end_time: i.end_time_string,
          room_name: i.room.name,
          user_name: (i.user && i.user.name) || "",
          start_time: i.start_time_string
        });

        if (unavailableTherapistKeys.includes(currentSlotKey)) {
          isConflicted = true;
        }
        if (unavailableTimeslotsKeys.includes(currentSlotKey)) {
          isConflicted = true;
        }

        return {
          ...i,
          period: { start_time_string: start, end_time_string: end },
          position: { top, left },
          assets: {
            height: `calc((${count * this.slotHeight}rem) - 2px)`,
            width: `calc((${this.slotWidth}) - 2px)`,
            background_color: i.secondary_color,
            border_color: i.primary_color,
            color: i.primary_color
          },
          error: isConflicted
        };
      });
    },
    existingAppointments() {
      const arr = [...this.clientAppointments];
      return arr.map(i => {
        const date = i.date;
        const start = i.start_time_string;
        const end = i.end_time_string;
        const count = this.getSlotCount(start, end);
        const { top, left } = this.getSlotPosition(date, start);

        return {
          ...i,
          period: { start_time_string: start, end_time_string: end },
          position: { top, left },
          assets: {
            height: `calc((${count * this.slotHeight}rem) - 2px)`,
            width: `calc((${this.slotWidth}) - 2px)`,
            background_color: "#F2F4F7",
            border_color: "#F2F4F7",
            color: "#667085"
          }
        };
      });
    },
    availableCredits() {
      if (!this.client || !this.client.id) return 0;
      if (!this.client.credit_detail) return 0;
      const details = this.client.credit_detail;
      const available = details.session_credits.available;
      const scheduled = details.scheduled_credits.total_credits || 0;
      return available - scheduled;
    },
    creditsUsed() {
      return this.bookingTimeslots.reduce((partialSum, i) => {
        const slotCount = this.getSlotCount(
          i.start_time_string,
          i.end_time_string
        );
        return partialSum + slotCount;
      }, 0);
    },
    availableCreditToUse() {
      const subs = this.availableCredits % this.creditsUsed;
      return this.availableCredits - subs;
    }
  },
  watch: {
    endAfterInput(val) {
      this.appointmentCountInput.disabled = val !== "appointment_count";
      if (this.recurrence.end_after !== val) {
        this.onUpdateRecurrence();
      }
    }
  },
  mounted() {
    this.intervalInput.model = this.recurrence.interval;
    this.endAfterInput = this.recurrence.end_after;
    this.appointmentCountInput.model =
      this.recurrence.appointment_count || this.bookingTimeslots.length;

    setTimeout(() => {
      if (!this.bookingTimeslots.length) {
        this.onScrollToTime(`07:00`);
        return;
      }
      const startTime = this.bookingTimeslots[0].start_time_string || "";

      let time = Number(startTime.split(":")[0]);
      if (time < 10) time = `0${time}`;
      this.onScrollToTime(`${time}:00`);
      if (!this.intervalInput.model) {
        this.intervalInput.model = 1;
      }
    }, 1000);
  },
  methods: {
    ...mapActions([""]),
    onInputInterval(payload) {
      this.intervalInput.model = payload.value;
      if (this.recurrence.interval !== payload.value) {
        this.onUpdateRecurrence();
      }
    },
    onInputAppointmentCount(payload) {
      this.appointmentCountInput.model = payload.value;
      this.appointmentCountInput.error = ``;
      if (payload.value < 1) {
        this.appointmentCountInput.error = `min. 1`;
      } else if (this.recurrence.appointment_count !== payload.value) {
        this.onUpdateRecurrence();
      }
    },
    onUpdateRecurrence() {
      const interval = this.intervalInput.model;
      const params = {
        process: "manage_schedule",
        interval,
        end_after: this.endAfterInput,
        appointment_count: this.appointmentCountInput.model
      };
      this.$emit("set-recurrence", params);
    },
    onScrollToTime(time) {
      const id = `recurring_time_view_${time}`;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
      }
    },
    getSlotPosition(date, start) {
      let top = "calc(12.5rem";
      let left = "calc(5rem";

      const indexOfTop = this.clockRound.findIndex(i => {
        const dt = i.split(":")[0];
        return dt === start.split(":")[0];
      });
      top += ` + ${indexOfTop * (this.slotHeight * 2)}rem`;
      if (start.split(":")[1] === "30") {
        top += ` + ${this.slotHeight}rem`;
      }
      top += " + 0.5px)";

      const indexOfLeft = this.headers.findIndex(i => i.value === date);
      if (indexOfLeft > -1) {
        const loop = indexOfLeft;
        for (let index = 0; index < loop; index++) {
          left += ` + ${this.slotWidth}`;
        }
      }
      left += " + 0.5px)";
      return { top, left };
    },
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/overlay/ManageScheduleRecurring.vue